import { DateTime } from "luxon";

export const JOURNAL_TIME_ZONE = "Asia/Dubai";

const calendarDate = (value: Date) => {
  const local = DateTime.fromJSDate(value);
  return DateTime.fromObject(
    { year: local.year, month: local.month, day: local.day },
    { zone: JOURNAL_TIME_ZONE }
  );
};

export const journalDayStartIso = (value: Date): string =>
  calendarDate(value).startOf("day").toUTC().toISO() ?? "";

export const journalDayEndIso = (value: Date): string =>
  calendarDate(value).endOf("day").toUTC().toISO() ?? "";

export const exceedsOneMonth = (start: Date, end: Date): boolean => {
  const startDay = calendarDate(start).startOf("day");
  const endDay = calendarDate(end).startOf("day");
  return endDay > startDay.plus({ months: 1 });
};

/**
 * Rolling month ending on `now` in Dubai, as local dates the range picker can
 * show. Luxon month subtraction can land on a day whose plus-one-month is
 * still before `now` (31 March minus one month is 28 February, and 28 February
 * plus one month is 28 March). Those days start later so the default always
 * passes `exceedsOneMonth`.
 */
export const defaultJournalRange = (
  now: DateTime = DateTime.now().setZone(JOURNAL_TIME_ZONE)
): { start: Date; end: Date } => {
  const end = now.setZone(JOURNAL_TIME_ZONE).startOf("day");
  let start = end.minus({ months: 1 });
  while (end > start.plus({ months: 1 })) {
    start = start.plus({ days: 1 });
  }
  return {
    start: new Date(start.year, start.month - 1, start.day),
    end: new Date(end.year, end.month - 1, end.day),
  };
};

export const dubaiToday = (
  now: DateTime = DateTime.now().setZone(JOURNAL_TIME_ZONE)
): string => now.setZone(JOURNAL_TIME_ZONE).toISODate() ?? "";

/** Inclusive Dubai calendar days a new posting date may use. */
export const journalPostingBounds = (
  now: DateTime = DateTime.now().setZone(JOURNAL_TIME_ZONE)
): { min: string; max: string } => {
  const today = now.setZone(JOURNAL_TIME_ZONE).startOf("day");
  let min = today.minus({ months: 1 });
  while (today > min.plus({ months: 1 })) {
    min = min.plus({ days: 1 });
  }
  return {
    min: min.toISODate() ?? "",
    max: today.toISODate() ?? "",
  };
};
